import { useState, useEffect, useCallback } from "react";

const slides = [
  {
    title: "Bogi Health Organization for Development (BHODEV)",
    subtitle: "The purpose of Human Life is to serve, show compassion and the will to help others",
    image: "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/4c1cd5f0-f0ba-469d-9d15-862b5fbc0c17_Home-banner---BOGI-HEALTH-ORGANIZATION-FOR-DEVELOPMENT---BHODEV.jpeg?v=5c484f002e213a147440e9f2b5e81a21",
  },
  {
    title: "What We Do is Meant to Last",
    subtitle: "We promote livelihood sustainability through empowering the rural communities.",
    image: "https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/1ea53a58-d287-408e-8919-d82193254383_Home-banner-2---BOGI-HEALTH-ORGANIZATION-FOR-DEVELOPMENT---BHODEV.jpeg?v=e739c5ac09b353d2e68c5b009f8f6889",
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSlide = useCallback((idx: number) => {
    if (isAnimating || idx === current) return;
    setIsAnimating(true);
    setCurrent(idx);
    setTimeout(() => setIsAnimating(false), 700);
  }, [current, isAnimating]);

  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((current + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [current, goToSlide]);

  const prev = () => goToSlide((current - 1 + slides.length) % slides.length);
  const next = () => goToSlide((current + 1) % slides.length);

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* Background slides with Ken Burns effect */}
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
            idx === current ? "opacity-100 scale-100" : "opacity-0 scale-105"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={slide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-6">
          {/* Decorative line */}
          <div className="w-12 h-0.5 bg-primary mb-2"></div>

          <h1
            key={`title-${current}`}
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-saria font-bold leading-tight animate-fade-in-up"
          >
            {slides[current].title}
          </h1>
          <p
            key={`sub-${current}`}
            className="text-white/80 text-base sm:text-lg md:text-xl max-w-xl leading-relaxed animate-fade-in-up"
            style={{ animationDelay: "0.15s" }}
          >
            {slides[current].subtitle}
          </p>
          <div
            className="flex flex-wrap items-center justify-center gap-3 mt-2 animate-fade-in-up"
            style={{ animationDelay: "0.3s" }}
          >
            <a
              href="#about"
              className="flex items-center gap-3 bg-primary text-white px-7 py-3 rounded-full hover:bg-primary-dark transition-all duration-300 font-inter font-semibold whitespace-nowrap cursor-pointer shadow-lg shadow-primary/30 hover:shadow-primary/50"
            >
              Learn more
              <span className="bg-white/20 w-7 h-7 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </a>
            <a
              href="/donate"
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-7 py-3 rounded-full hover:bg-white/20 transition-all duration-300 font-inter font-semibold whitespace-nowrap cursor-pointer"
            >
              <i className="ri-heart-fill text-primary"></i>
              Donate Now
            </a>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        className="absolute top-1/2 left-4 md:left-8 -translate-y-1/2 bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer z-20 border border-white/20"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute top-1/2 right-4 md:right-8 -translate-y-1/2 bg-white/10 hover:bg-white/25 backdrop-blur-sm text-white w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer z-20 border border-white/20"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`h-2 rounded-full transition-all duration-500 cursor-pointer ${
              idx === current ? "bg-primary w-8" : "bg-white/40 w-2 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/50 text-lg"></i>
      </div>
    </section>
  );
}